/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from 'tooltip';
import Store from '../../store/store';
import { resultCardId } from '../../utils/string';
import Holon from '../Buttons/holon';
import modalTypes from '../../constants/modalTypes';
import { openModal } from '../../actions/modalActions';
import { determineModalType } from '../../utils/resultModalHelpers';

const ParentCard = props => {
  const { result } = props;
  const config = { showDelay: 50 };
  Tooltip(config);
  const { defaultOptions } = Store.getState().defaultOptionsReducer;
  const { users } = Store.getState().userReducer;
  const owner = users.find(user => user.id === result.owner_id) || {};
  const initials =
    owner && owner.name
      ? owner.name
          .split(' ')
          .map(n => n.charAt(0))
          .join('')
      : null;
  const isDefect = result.result_type && result.result_type.kind === 'Defect';
  let valueDefaults =
    defaultOptions.value_defaults && defaultOptions.value_defaults.values;
  if (isDefect) {
    valueDefaults =
      defaultOptions.severity_defaults &&
      defaultOptions.severity_defaults.values;
  }
  const riskDefaults =
    defaultOptions.risk_defaults && defaultOptions.risk_defaults.values;
  const sizeDefaults =
    defaultOptions.size_defaults && defaultOptions.size_defaults.values;
  const tShirtSizeDefaults =
    defaultOptions.t_shirt_size_defaults &&
    defaultOptions.t_shirt_size_defaults.values;
  const vrs = result.result_type ? result.result_type.type_attributes : {};
  let vrsValue = '-';
  if (
    valueDefaults &&
    vrs.value &&
    valueDefaults[vrs.value] &&
    valueDefaults[vrs.value].length > 0
  )
    vrsValue = isDefect
      ? `${valueDefaults[vrs.value][0]}${vrs.value}`
      : valueDefaults[vrs.value][0];
  const width = props.isFirst ? '100%' : `${10 + (result.level - 1) * 5}em`;

  let background = '#CFCFD1';
  let headingColor = '#B1B2B4';
  let borderColor = '#767677';
  switch (result.level) {
    case 2:
      background = '#B9B9BD';
      headingColor = '#9FA0A3';
      borderColor = '#4C4E4E ';
      break;
    case 3:
      background = '#A5A5A9';
      headingColor = '#909296';
      borderColor = '#747474';
      break;
    case 4:
      background = '#8F8F94';
      headingColor = '#7E8185';
      borderColor = '#767677';
      break;
    default:
      background = '#CFCFD1';
      headingColor = '#B1B2B4';
      borderColor = '#767677';
  }

  let panelHeader = (
    <div
      className="panel-heading"
      style={{ borderColor: headingColor, backgroundColor: headingColor }}
    >
      <div className="panel-title">
        <div style={{ width: '33%', display: 'inline-block' }}>
          <a
            style={{ color: '#000000' }}
            onClick={() =>
              Store.dispatch(
                openModal(determineModalType(result), {
                  id: result.id,
                  result: result.temporaryId ? result : undefined
                })
              )
            }
          >
            {result.id ? resultCardId(result) : 'New'}
          </a>
        </div>
        <div
          style={{ width: '33%', textAlign: 'center', display: 'inline-block' }}
        >
          {result.parent_id && (
            <Holon
              action={e => {
                e.stopPropagation();
                return props.isFirst ? null : props.addParent(result.parent_id);
              }}
            />
          )}
        </div>
        <div
          className="text-center value"
          style={{
            width: '11%',
            display: 'inline-block',
            borderRight: `1px solid ${headingColor}`,
            marginLeft: '1px'
          }}
        >
          {vrsValue}
        </div>
        <div
          className="text-center risk"
          style={{
            width: '11%',
            display: 'inline-block',
            borderRight: `1px solid ${headingColor}`
          }}
        >
          {riskDefaults && vrs.risk ? riskDefaults[vrs.risk][0] : '-'}
        </div>
        <div
          className="text-center points"
          style={{ width: '11%', display: 'inline-block' }}
        >
          {sizeDefaults && vrs.size ? sizeDefaults[vrs.size] : '-'}
        </div>
      </div>
    </div>
  );

  if (result.level > 1) {
    panelHeader = (
      <div
        className="panel-heading"
        style={{ borderColor: headingColor, backgroundColor: headingColor }}
      >
        <div className="panel-title" style={{ width: '100%' }}>
          <div style={{ width: '33%', display: 'inline-block' }}>
            <a
              style={{ color: result.level === 4 ? 'white' : '#000000' }}
              onClick={() => {
                if (result.id && result.id > 0) {
                  Store.dispatch(
                    openModal(determineModalType(result), { id: result.id })
                  );
                }
              }}
            >
              {result.id ? resultCardId(result) : 'New'}
            </a>
          </div>
          <div
            style={{
              width: '33%',
              textAlign: 'center',
              display: 'inline-block'
            }}
          >
            {result.parent_id && (
              <Holon
                action={e => {
                  e.stopPropagation();
                  return props.isFirst
                    ? null
                    : props.addParent(result.parent_id);
                }}
              />
            )}
          </div>
          <div
            className="text-center points"
            style={{ width: '1.7em', float: 'right', display: 'inline-block' }}
          >
            {tShirtSizeDefaults && vrs.size
              ? tShirtSizeDefaults[vrs.size].substring(0, 2)
              : '-'}
          </div>
          <div
            className="text-center risk"
            style={{
              width: '1.7em',
              float: 'right',
              display: 'inline-block',
              borderRight: `1px solid ${headingColor}`
            }}
          >
            {vrs.rroe || '-'}
          </div>
          <div
            className="text-center tc"
            style={{
              width: '1.7em',
              float: 'right',
              display: 'inline-block',
              borderRight: `1px solid ${headingColor}`
            }}
          >
            {vrs.tc || '-'}
          </div>
          <div
            className="text-center value"
            style={{
              width: '1.7em',
              float: 'right',
              display: 'inline-block',
              borderRight: `1px solid ${headingColor}`,
              marginLeft: '-16px'
            }}
          >
            {vrs.value || '-'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`panel panel-default wall-row parent-card ${
        result.blocked ? 'blocked-card' : ''
      }`}
      style={{
        height: 'initial',
        minHeight: props.isFirst ? '125px' : 'initial',
        maxWidth: props.isFirst ? '100vw' : '30vw',
        minWidth: `171px`,
        width: props.isFirst ? '200px' : 'fit-content',
        borderColor: '#6F6E6F',
        backgroundColor: props.isFirst ? '#EDEDED' : background,
        borderRadius: '0px'
      }}
    >
      {panelHeader}
      <div
        className="panel-body"
        style={{
          backgroundColor: props.isFirst ? '#EDEDED' : background,
          padding: '0.2em 2.1em',
          minHeight: '6em',
          marginTop: '3px',
          borderRadius: '0px'
        }}
      >
        {!props.isFirst ? props.children : null}
        <p
          style={{
            width,
            textAlign: 'center',
            margin: 'auto',
            maxHeight: props.isFirst ? '100%' : '1.5em',
            whiteSpace: props.isFirst ? 'none' : 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            marginTop: '5px',
            height: '66px',
            color: result.level === 4 ? 'white' : 'black',
            display: 'block'
          }}
          data-tooltip={result.name || result.short_name}
          data-tooltip-positions="left;top;bottom;right"
        >
          {result.name || result.short_name}
        </p>
        {!props.isFirst && (
          <button
            type="button"
            className="btn btn-link right"
            style={{
              position: 'relative',
              left: '24px',
              marginTop: '-22px',
              top: '7px',
              color: 'white'
            }}
            onClick={e => {
              e.stopPropagation();
              return props.removeParent(result.id);
            }}
          >
            &times;
          </button>
        )}
      </div>
      <div className="panel-footer">
        {props.isFirst &&
          (initials === '' || initials === null) && (
            <div className="initial right">&nbsp;</div>
          )}
        {props.isFirst &&
          initials && <div className="initial right">{initials}</div>}
      </div>
    </div>
  );
};

ParentCard.propTypes = {
  result: PropTypes.object.isRequired
};

export default ParentCard;
